import type {
  FlotiqEventApiResponse,
  FlotiqGeoSearchParams,
  FlotiqHorecaApiResponse,
  FlotiqStore,
  FlotiqStoreApiResponse as BaseFlotiqStoreApiResponse,
  FlotiqStoreDetailApiResponse,
} from "@jti/fe-common";
import { getConfig } from "@jti/fe-common";

import { Rest } from "../Rest";

const DEFAULT_LIMIT = 1000;
const ORDER_BY = "order_by";

const FLOTIQ_CONTENT_TYPES = {
  store: "Store",
  event: "event",
  horeca: "horeca_location",
} as const;

export type AggregateByCategory = "State_slug" | "City_slug";

type PaginatedResponse = {
  data: unknown[];
  total_count: number;
  total_pages: number;
  current_page: number;
  count: number;
};

type FlotiqStoreApiResponse = BaseFlotiqStoreApiResponse &
  PaginatedResponse & {
    summary: {
      aggregations: Record<
        string,
        {
          buckets: {
            key: string;
            doc_count: number;
          }[];
        }
      >;
    };
  };

type FlotiqAreaApiResponse = {
  data: {
    id: string;
    name: string;
    slug: string;
    map_zoom: number;
    center_coordinates: { lat: number; lon: number };
  }[];
} & PaginatedResponse;

export type FlotiqContentListResponse<T> = {
  total_count: number;
  total_pages: number;
  current_page: number;
  count: number;
  data: T[];
};

export class FlotiqApi extends Rest {
  readonly name = "FlotiqApi";
  readonly apiKey: string;
  readonly contentType: string;

  constructor(
    baseUrl = "/",
    apiKey = getConfig().FLOTIQ_API_KEY,
    contentType = getConfig().STORE_LOCATOR_CONTENT_TYPE ?? FLOTIQ_CONTENT_TYPES.store
  ) {
    super(baseUrl);
    this.apiKey = apiKey;
    this.contentType = contentType;
    this.setCommonHeader("X-AUTH-TOKEN", this.apiKey);
  }

  static init(apiKey?: string, contentType?: string): FlotiqApi {
    return new FlotiqApi(
      getConfig().FLOTIQ_API_URL,
      apiKey ?? getConfig().FLOTIQ_API_KEY,
      contentType ?? getConfig().STORE_LOCATOR_CONTENT_TYPE
    );
  }

  private buildGeoSearchParams({
    radius,
    lat,
    lng,
    limit = DEFAULT_LIMIT,
    orderBy,
    contentType,
  }: FlotiqGeoSearchParams): URLSearchParams {
    const params = new URLSearchParams({
      q: "*",
      "geo_filters[Location]": `geo_distance,${radius}m,${lat},${lng}`,
      "content_type[]": contentType,
      limit: `${limit}`,
    });

    if (orderBy) {
      params.append(ORDER_BY, orderBy);
    }

    return params;
  }

  private async searchByLocation<TResponse>({
    radius,
    lat,
    lng,
    limit = DEFAULT_LIMIT,
    orderBy,
    contentType,
  }: FlotiqGeoSearchParams): Promise<TResponse> {
    const params = this.buildGeoSearchParams({ radius, lat, lng, limit, orderBy, contentType });

    return this.request<TResponse, TResponse>(`/api/v1/search?${params.toString()}`, {
      method: "GET",
    });
  }

  async getStores({
    radius,
    lat,
    lng,
    storesLimit = DEFAULT_LIMIT,
    orderBy,
  }: {
    radius: number;
    lat: number;
    lng: number;
    storesLimit?: number;
    orderBy?: string;
  }): Promise<FlotiqStoreApiResponse> {
    return await this.searchByLocation<FlotiqStoreApiResponse>({
      radius,
      lat,
      lng,
      limit: storesLimit,
      orderBy,
      contentType: this.contentType,
    });
  }

  async getEvents({
    radius,
    lat,
    lng,
    eventsLimit = DEFAULT_LIMIT,
    orderBy,
  }: {
    radius: number;
    lat: number;
    lng: number;
    eventsLimit?: number;
    orderBy?: string;
  }): Promise<FlotiqEventApiResponse> {
    return await this.searchByLocation<FlotiqEventApiResponse>({
      radius,
      lat,
      lng,
      limit: eventsLimit,
      orderBy,
      contentType: FLOTIQ_CONTENT_TYPES.event,
    });
  }

  async getHoreca({
    radius,
    lat,
    lng,
    horecaLimit = DEFAULT_LIMIT,
    orderBy,
  }: {
    radius: number;
    lat: number;
    lng: number;
    horecaLimit?: number;
    orderBy?: string;
  }): Promise<FlotiqHorecaApiResponse> {
    return await this.searchByLocation<FlotiqHorecaApiResponse>({
      radius,
      lat,
      lng,
      limit: horecaLimit,
      orderBy,
      contentType: FLOTIQ_CONTENT_TYPES.horeca,
    });
  }

  async getAggregatedStores({
    aggregateByQuery,
    filterCategory,
    filterQuery,
    limit = 1,
  }: {
    aggregateByQuery?: AggregateByCategory;
    filterCategory?: AggregateByCategory;
    filterQuery?: string;
    limit?: number;
  }): Promise<FlotiqStoreApiResponse> {
    const params = new URLSearchParams({
      q: "*",
      limit: limit.toString(),
    });

    if (filterCategory && filterQuery) {
      params.set(`filters[${filterCategory}]`, filterQuery);
    }

    if (aggregateByQuery) {
      params.set("aggregate_by[]", aggregateByQuery);
    }

    return this.request<FlotiqStoreApiResponse>(`/api/v1/search?${params.toString()}`, { method: "GET" });
  }

  async getFilteredStores({
    filters,
    limit = DEFAULT_LIMIT,
  }: {
    filters: Array<{ category: AggregateByCategory; query: string }>;
    limit?: number;
  }): Promise<FlotiqStoreApiResponse> {
    const citySlug = filters.find((f) => f.category === "City_slug")?.query;
    const q = citySlug !== undefined ? `"${citySlug}"` : "*";

    return this.fetchAllPages<FlotiqStoreApiResponse>({
      buildUrl: (page) => {
        const params = new URLSearchParams({
          q,
          "fields[]": "City_slug",
          query_mode: "exact",
          limit: limit.toString(),
          page: page.toString(),
        });

        for (const filter of filters) {
          params.append(`filters[${filter.category}]`, filter.query);
        }

        return `/api/v1/search?${params.toString()}`;
      },
      limit,
    });
  }

  async getAllStoresSearchPaged(opts?: {
    limit?: number;
  }): Promise<FlotiqContentListResponse<Omit<FlotiqStore, "address"> & Record<string, unknown>>> {
    const limit = opts?.limit ?? 30000;
    return this.fetchAllPages<FlotiqContentListResponse<Omit<FlotiqStore, "address"> & Record<string, unknown>>>({
      buildUrl: (page) => {
        const params = new URLSearchParams({ limit: limit.toString(), page: page.toString() });
        return `/api/v1/content/store?${params.toString()}`;
      },
      limit,
      maxItems: 30000,
    });
  }

  async getAllStates({ limit = DEFAULT_LIMIT }: { limit?: number } = {}): Promise<FlotiqAreaApiResponse> {
    return this.fetchAllPages<FlotiqAreaApiResponse>({
      buildUrl: (page) => {
        const params = new URLSearchParams({ limit: limit.toString(), page: page.toString() });
        return `/api/v1/content/state?${params.toString()}`;
      },
      limit,
    });
  }

  async getAllCities({ limit = DEFAULT_LIMIT }: { limit?: number } = {}): Promise<FlotiqAreaApiResponse> {
    return this.fetchAllPages<FlotiqAreaApiResponse>({
      buildUrl: (page) => {
        const params = new URLSearchParams({ limit: limit.toString(), page: page.toString() });
        return `/api/v1/content/city?${params.toString()}`;
      },
      limit,
    });
  }

  async getStoreByExternalId(externalId: string): Promise<FlotiqStoreApiResponse> {
    const params = new URLSearchParams({
      q: "*",
      "filters[ExternalId]": externalId,
      "content_type[]": this.contentType,
    });
    return this.request<FlotiqStoreApiResponse>(`/api/v1/search?${params.toString()}`, { method: "GET" });
  }

  async getStoreById(storeId: string): Promise<FlotiqStoreDetailApiResponse> {
    return this.request<FlotiqStoreDetailApiResponse>(`/api/v1/content/${this.contentType}/${storeId}`, {
      method: "GET",
    });
  }

  private async fetchAllPages<T extends PaginatedResponse>({
    buildUrl,
    limit = DEFAULT_LIMIT,
    maxItems = 10000,
  }: {
    buildUrl: (page: number) => string;
    limit?: number;
    maxItems?: number;
  }): Promise<T> {
    // Flotiq API doesn't allow fetching more data than 10000 items
    // Page * limit should be less than 10000
    // Content API can be higher.
    const firstResponse = await this.request<T, T>(buildUrl(1), { method: "GET" });

    if (firstResponse.data.length >= firstResponse.total_count) {
      return firstResponse;
    }

    const remainingPages = Array.from({ length: firstResponse.total_pages - 1 }, (_, i) => i + 2).filter(
      (pageIndex) => pageIndex * limit < maxItems
    );
    const remainingResponses = await Promise.all(
      remainingPages.map((page) => this.request<T, T>(buildUrl(page), { method: "GET" }))
    );

    const allData = [firstResponse.data, ...remainingResponses.map((r) => r.data)].flat();
    return { ...firstResponse, data: allData };
  }
}
